import { LoadingOutlined } from '@ant-design/icons';
import { cn } from '@components/shadcn/utils';
import { ImageryButton } from '@components/shared/ImageryButton/ImageryButton';
import { Switch } from '@components/shared/Switch';
import { useOrganization } from '@hooks/queries/useOrganization';
import { useProperty } from '@hooks/queries/useProperty';
import { useConfirm } from '@hooks/useConfirm';
import { useFetchUploadedCropSeason } from '@hooks/useFetchUploadedCropSeason';
import { usePrescriptionCreateStore } from '@screens/Prescriptions/PrescriptionCreate/prescriptionCreate.store';
import {
  selectActiveSeasons,
  selectInactiveSeasons,
  selectSeason,
  selectUploadedSeasons,
} from '@state/season/season.selectors';
import { buildFarmSettingsUrl } from '@utils/farmSettings.utils';
import { generateCropSeasonGroups } from '@utils/seasons.utils';
import { Divider, Radio, Spin } from 'antd';
import type { RadioChangeEvent } from 'antd/lib/radio';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router';
import { useBoundStore } from 'src/state/store';
import type { FieldSeasonData } from '../../../core/models/seasons';
import { routePaths } from '../../../screens/routes';

interface Props {
  close: () => void;
  visible: boolean;
}

const SEASON_MODES = ['active_seasons', 'unique_season'] as const;
type SeasonMode = typeof SEASON_MODES[number];

export const SeasonPopover = (props: Props) => {
  const { t } = useTranslation();
  const { mode, displayWithoutActiveSeason, updateOptions } = useBoundStore(
    (state) => state.seasons
  );

  const { setSelectedField } = useBoundStore((state) => state.regions);

  const selectedSeason = useBoundStore(selectSeason);
  const activeSeasons = useBoundStore(selectActiveSeasons);
  const inactiveSeasons = useBoundStore(selectInactiveSeasons);
  const uploadedSeasons = useBoundStore(selectUploadedSeasons);
  const confirm = useConfirm();
  const { organization } = useOrganization();
  const { property } = useProperty();
  const { isLoading: isLoadingUploadedSeasons } = useFetchUploadedCropSeason(
    organization?.id as string
  );

  const { step: creationStep, reset } = usePrescriptionCreateStore();

  const isCreatingPrescriptions =
    Boolean(useMatch(routePaths.prescriptionsCreate)) && creationStep > 0;
  const isOnYieldAnalysis = useMatch({ path: routePaths.yieldAnalysis, end: false });

  // Local state
  const [localDisplayFieldsWithoutSeason, setLocalDisplayFieldsWithoutSeason] = useState(
    displayWithoutActiveSeason
  );
  const [localMode, setLocalMode] = useState<SeasonMode>(mode);
  const [localSelectedSeason, setLocalSelectedSeason] = useState<FieldSeasonData | undefined>(
    selectedSeason
  );
  const [draftSource, setDraftSource] = useState({
    mode,
    selectedSeason,
    displayWithoutActiveSeason,
    visible: props.visible,
  });

  // Re-sync the drafts with the store whenever the popover reopens or the store changes
  if (
    draftSource.mode !== mode ||
    draftSource.selectedSeason !== selectedSeason ||
    draftSource.displayWithoutActiveSeason !== displayWithoutActiveSeason ||
    draftSource.visible !== props.visible
  ) {
    setDraftSource({ mode, selectedSeason, displayWithoutActiveSeason, visible: props.visible });
    setLocalMode(mode);
    setLocalSelectedSeason(selectedSeason);
    setLocalDisplayFieldsWithoutSeason(displayWithoutActiveSeason);
  }

  const handleSeasonRadioGroupChange = (e: RadioChangeEvent) => {
    setLocalSelectedSeason(e.target.value);
    setLocalMode(SEASON_MODES[1]);
  };

  const handleModeRadioGroupChange = (e: RadioChangeEvent) => {
    const newMode = e.target.value;
    setLocalMode(e.target.value);
    if (newMode === SEASON_MODES[0]) {
      setLocalSelectedSeason(undefined);
    } else {
      setLocalSelectedSeason(activeSeasons[0]);
    }
  };

  const handleSave = async () => {
    if (isCreatingPrescriptions) {
      const confirmed = await confirm({
        title: t('common.unsaved_changes.title'),
        message: t('common.unsaved_changes.description'),
        confirmText: t('common.unsaved_changes.discard'),
        danger: true,
      });
      // User chose to keep editing — abort the apply so nothing is discarded or closed.
      if (!confirmed) return;
      reset();
    }

    updateOptions({
      mode: localSelectedSeason ? SEASON_MODES[1] : SEASON_MODES[0],
      selectedSeasonId: localSelectedSeason?.season.id,
      displayWithoutActiveSeason: localDisplayFieldsWithoutSeason,
    });

    setSelectedField(null);

    props.close();
  };

  const handleCancel = () => {
    props.close();
  };

  const cropsGroups = useMemo((): {
    [key: string]: {
      groups: { [key: string]: FieldSeasonData[] };
      isEmpty: boolean;
    };
  } => {
    return {
      active: {
        groups: generateCropSeasonGroups(activeSeasons),
        isEmpty: activeSeasons.length === 0,
      },
      inactive: {
        groups: generateCropSeasonGroups(inactiveSeasons),
        isEmpty: inactiveSeasons.length === 0,
      },
      ...(isOnYieldAnalysis && {
        uploaded: {
          groups: generateCropSeasonGroups(uploadedSeasons),
          isEmpty: uploadedSeasons.length === 0,
        },
      }),
    };
  }, [activeSeasons, inactiveSeasons, uploadedSeasons, isOnYieldAnalysis]);

  return (
    <div className="w-[320px]">
      <div>
        <div>
          <div className="flex items-start gap-2 px-3 py-1">
            <Switch
              checked={localDisplayFieldsWithoutSeason}
              onChange={() => setLocalDisplayFieldsWithoutSeason(!localDisplayFieldsWithoutSeason)}
              className="mt-1"
            />
            <p>{t('header.seasons.all')}</p>
          </div>
          {!isOnYieldAnalysis && (
            <Radio.Group value={localMode} onChange={handleModeRadioGroupChange} className="grid">
              <Radio value={SEASON_MODES[0]} className="py-1.5 px-3">
                {t('header.seasons.active_seasons')}
              </Radio>
            </Radio.Group>
          )}
          <Divider className="my-1" />
          <div
            className="max-h-[300px] overflow-auto flex flex-col sy-scrollbar sy-scrollbar-light"
            data-testid="crop-seasons-groups"
          >
            <div className="flex flex-col">
              {Object.keys(cropsGroups).map((groupLabel) => {
                const disabled = !isOnYieldAnalysis && groupLabel === 'uploaded';

                return (
                  <div
                    key={groupLabel}
                    className={cn('order-2', {
                      ['order-1 bg-sy-blue-10']: isOnYieldAnalysis && groupLabel === 'uploaded',
                    })}
                  >
                    <div className="py-1 px-2">
                      <span
                        className={cn('text-xs font-bold capitalize text-sy-neutral-60', {
                          ['order-1 text-sy-blue-60 border-']:
                            isOnYieldAnalysis && groupLabel === 'uploaded',
                        })}
                      >
                        {t(`header.seasons.group_title.${groupLabel}`)}
                      </span>
                      {groupLabel === 'uploaded' && isLoadingUploadedSeasons && (
                        <Spin
                          className="pl-2"
                          indicator={
                            <LoadingOutlined
                              spin
                              onPointerEnterCapture={undefined}
                              onPointerLeaveCapture={undefined}
                            />
                          }
                        />
                      )}
                    </div>
                    {!cropsGroups[groupLabel].isEmpty &&
                      Object.entries(cropsGroups[groupLabel].groups).map(([key, seasonDatas]) => {
                        const [year, nextYear] = key.split('__');

                        return (
                          <div className="px-2" key={`${groupLabel}_${key}`}>
                            <div className="py-1">
                              <span
                                className={cn('text-xs font-semibold text-sy-neutral-60', {
                                  ['opacity-50']: disabled,
                                })}
                              >
                                {t('common.season')} {year}
                                {nextYear ? `-${nextYear.substring(2)}` : ''}
                              </span>
                            </div>
                            <Radio.Group
                              disabled={disabled}
                              className="grid"
                              name="radiogroup"
                              value={localSelectedSeason}
                              onChange={handleSeasonRadioGroupChange}
                            >
                              {seasonDatas.map((seasonData) => {
                                return (
                                  <Radio
                                    key={seasonData.id}
                                    value={seasonData}
                                    className="px-1 py-1"
                                  >
                                    {seasonData.season.name}
                                  </Radio>
                                );
                              })}
                            </Radio.Group>
                          </div>
                        );
                      })}
                    {cropsGroups[groupLabel].isEmpty && (
                      <div className="py-1.5 px-3 ml-2">
                        <span className="text-xs font-semibold">
                          {t(`header.seasons.empty.${groupLabel}`)}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <Divider className="my-1" />
      <div className="flex gap-2 items-center justify-between px-3 py-2">
        <a
          target="_blank"
          rel="noreferrer"
          href={buildFarmSettingsUrl(organization?.id, property?.id)}
        >
          {t('header.seasons.footer')}
        </a>
        <div className="ml-auto flex gap-2">
          <ImageryButton type="outline" onClick={handleCancel}>
            {t('common.cancel')}
          </ImageryButton>
          <ImageryButton type="primary" onClick={handleSave}>
            {t('common.apply')}
          </ImageryButton>
        </div>
      </div>
    </div>
  );
};
